import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AdminCompanyContext } from '../../AdminApp';
import { getAdminTraces, getAdminTraceSpans } from '../../adminApi';
import { useCompanyNames, resolveCompanyName } from '../../hooks/useCompanyNames';

const PAGE_SIZE = 50;

const PERIODS = [
  { label: '1h',  value: '1h' },
  { label: '24h', value: '24h' },
  { label: '7d',  value: '7d' },
  { label: 'All', value: '' },
];

// '' means all sources: the API treats an absent `source` as no filter.
const SOURCES = [
  { label: 'All sources', value: '' },
  { label: 'Chat',        value: 'chat' },
  { label: 'Describe',    value: 'describe' },
];

const SPAN_TYPE_ICON = { chain: '⬡', llm: '◈', tool: '⚙' };
const SPAN_TYPE_COLOR = { chain: '#6366f1', llm: '#2563eb', tool: '#16a34a' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sinceFromPeriod(period) {
  if (!period) return undefined;
  const now = new Date();
  if (period === '1h')  now.setHours(now.getHours() - 1);
  if (period === '24h') now.setDate(now.getDate() - 1);
  if (period === '7d')  now.setDate(now.getDate() - 7);
  return now.toISOString();
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function fmtMs(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function shortId(id) {
  if (!id) return '—';
  return id.length > 14 ? id.slice(0, 6) + '…' + id.slice(-4) : id;
}

function truncate(str, max = 80) {
  if (!str) return '—';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

// Traces recorded before the source column existed carry no source and are chat.
function traceSource(trace) {
  return trace.source || 'chat';
}

function SourceBadge({ source }) {
  return (
    <span className={`admin-source-badge ${source}`}>
      {source === 'describe' ? 'Describe' : 'Chat'}
    </span>
  );
}

// Build parent→children tree from flat spans array
function buildSpanTree(spans) {
  const map = {};
  spans.forEach(s => { map[s.span_id] = { ...s, children: [] }; });
  const roots = [];
  spans.forEach(s => {
    if (s.parent_span_id && map[s.parent_span_id]) {
      map[s.parent_span_id].children.push(map[s.span_id]);
    } else {
      roots.push(map[s.span_id]);
    }
  });
  return roots;
}

// ---------------------------------------------------------------------------
// SpanGantt — horizontal timeline bars for top-level node spans
// ---------------------------------------------------------------------------

function SpanGantt({ spans, traceStartedAt, traceDurationMs }) {
  if (!traceDurationMs || traceDurationMs <= 0) return null;

  // Only show direct children of root (the LangGraph nodes) in the Gantt
  const rootSpan = spans.find(s => !s.parent_span_id);
  if (!rootSpan) return null;
  const nodeSpans = spans.filter(
    s => s.parent_span_id === rootSpan.span_id && s.span_type === 'chain'
  );
  if (nodeSpans.length === 0) return null;

  const traceStart = new Date(traceStartedAt).getTime();

  return (
    <div className="trace-gantt">
      <div className="trace-gantt-label">Timeline</div>
      {nodeSpans.map(span => {
        const start = new Date(span.started_at).getTime();
        const end = span.ended_at ? new Date(span.ended_at).getTime() : start + (span.duration_ms || 0);
        const left = Math.max(0, ((start - traceStart) / traceDurationMs) * 100);
        const width = Math.max(1, ((end - start) / traceDurationMs) * 100);
        return (
          <div key={span.span_id} className="trace-gantt-row">
            <span className="trace-gantt-name">{span.name}</span>
            <div className="trace-gantt-track">
              <div
                className={`trace-gantt-bar${span.error ? ' error' : ''}`}
                style={{ left: `${left}%`, width: `${Math.min(width, 100 - left)}%` }}
                title={`${span.name} — ${fmtMs(span.duration_ms)}`}
              />
            </div>
            <span className="trace-gantt-ms">{fmtMs(span.duration_ms)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SpanNode — recursive tree node
// ---------------------------------------------------------------------------

function SpanNode({ span, depth = 0 }) {
  const [open, setOpen] = useState(depth < 2);
  const [showIO, setShowIO] = useState(false);
  const hasChildren = span.children && span.children.length > 0;
  const hasIO = span.input || span.output || span.error || (span.metadata && Object.keys(span.metadata).length > 0);
  const color = SPAN_TYPE_COLOR[span.span_type] || '#888';
  const icon = SPAN_TYPE_ICON[span.span_type] || '○';

  function handleRowClick(e) {
    // Toggle children if clicking the toggle arrow area; otherwise toggle details
    if (e.target.closest('.trace-span-toggle') && hasChildren) {
      setOpen(o => !o);
    } else if (hasIO) {
      setShowIO(v => !v);
    } else if (hasChildren) {
      setOpen(o => !o);
    }
  }

  return (
    <div className="trace-span-node" style={{ '--depth': depth }}>
      <div
        className={`trace-span-row${span.error ? ' error' : ''}${hasIO ? ' has-details' : ''}`}
        onClick={handleRowClick}
        style={{ cursor: hasIO || hasChildren ? 'pointer' : 'default' }}
      >
        <span className="trace-span-toggle" onClick={e => { if (hasChildren) { e.stopPropagation(); setOpen(o => !o); } }}>
          {hasChildren ? (open ? '▾' : '▸') : '·'}
        </span>
        <span className="trace-span-type-badge" style={{ color, borderColor: color }}>
          {icon} {span.span_type}
        </span>
        <span className="trace-span-name">{span.name}</span>
        <span className="trace-span-dur">{fmtMs(span.duration_ms)}</span>
        {span.error && <span className="trace-span-error-badge">error</span>}
        {hasIO && (
          <span className={`trace-span-expand-hint${showIO ? ' open' : ''}`} title="Click row to expand details">
            ❯
          </span>
        )}
      </div>

      {showIO && (
        <div className="trace-span-io" style={{ '--depth': depth }}>
          {span.error && (
            <div className="trace-span-io-block error">
              <strong>Error</strong>
              <pre>{span.error}</pre>
            </div>
          )}
          {span.input != null && (
            <div className="trace-span-io-block">
              <strong>Input</strong>
              <pre>{typeof span.input === 'string' ? span.input : JSON.stringify(span.input, null, 2)}</pre>
            </div>
          )}
          {span.output != null && (
            <div className="trace-span-io-block">
              <strong>Output</strong>
              <pre>{typeof span.output === 'string' ? span.output : JSON.stringify(span.output, null, 2)}</pre>
            </div>
          )}
          {span.metadata && Object.keys(span.metadata).length > 0 && (
            <div className="trace-span-io-block">
              <strong>Metadata</strong>
              <pre>{JSON.stringify(span.metadata, null, 2)}</pre>
            </div>
          )}
        </div>
      )}

      {open && hasChildren && (
        <div className="trace-span-children">
          {span.children.map(child => (
            <SpanNode key={child.span_id} span={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TraceDetail — expands below the selected row
// ---------------------------------------------------------------------------

function TraceDetail({ trace, onClose }) {
  const [spans, setSpans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    getAdminTraceSpans(trace.trace_id)
      .then(data => setSpans(data.spans || []))
      .catch(err => setError(err.message || 'Failed to load spans'))
      .finally(() => setLoading(false));
  }, [trace.trace_id]);

  const tree = spans ? buildSpanTree(spans) : [];

  return (
    <div className="trace-detail">
      <div className="trace-detail-header">
        <div className="trace-detail-meta">
          <span className="trace-detail-id" title={trace.trace_id}>
            Trace {shortId(trace.trace_id)}
          </span>
          <span className="trace-detail-time">{fmtDate(trace.started_at)}</span>
          <span>{fmtMs(trace.duration_ms)}</span>
          <span>{(trace.total_tokens || 0).toLocaleString()} tokens</span>
          {trace.node_path && trace.node_path.length > 0 && (
            <span className="trace-node-path">
              {trace.node_path.map((n, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="trace-node-arrow">→</span>}
                  <span className="trace-node-pill">{n}</span>
                </React.Fragment>
              ))}
            </span>
          )}
        </div>
        <button className="trace-detail-close" onClick={onClose}>✕</button>
      </div>

      {trace.question && (
        <div className="trace-detail-question">
          <strong>Question:</strong> {trace.question}
        </div>
      )}

      {loading && <div className="trace-detail-loading">Loading spans…</div>}
      {error && <div className="admin-error-state"><p>{error}</p></div>}

      {!loading && !error && spans && (
        <>
          <SpanGantt
            spans={spans}
            traceStartedAt={trace.started_at}
            traceDurationMs={trace.duration_ms}
          />
          <div className="trace-span-tree">
            {tree.map(root => (
              <SpanNode key={root.span_id} span={root} depth={0} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminTraces — main screen
// ---------------------------------------------------------------------------

export default function AdminTraces() {
  const { selectedCompany } = useContext(AdminCompanyContext);
  const companyNames = useCompanyNames();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [period, setPeriod] = useState('24h');
  const [company, setCompany] = useState('');
  const [source, setSource] = useState('');
  const [selectedTrace, setSelectedTrace] = useState(null);

  useEffect(() => {
    if (selectedCompany !== undefined) setCompany(selectedCompany || '');
  }, [selectedCompany]);

  const load = useCallback(async () => {
    setLoading(true);
    setSelectedTrace(null);
    try {
      const data = await getAdminTraces({
        company_id: company || undefined,
        source: source || undefined,
        since: sinceFromPeriod(period),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load traces');
    } finally {
      setLoading(false);
    }
  }, [company, source, period, page]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 0 when filters change
  const handlePeriod = p => { setPeriod(p); setPage(0); };
  const handleCompany = e => { setCompany(e.target.value); setPage(0); };
  const handleSource = e => { setSource(e.target.value); setPage(0); };

  const pageCount = Math.ceil(total / PAGE_SIZE);

  // Stats from current page
  const successCount = items.filter(t => t.success).length;
  const avgDur = items.length
    ? Math.round(items.reduce((s, t) => s + (t.duration_ms || 0), 0) / items.length)
    : 0;
  const avgTokens = items.length
    ? Math.round(items.reduce((s, t) => s + (t.total_tokens || 0), 0) / items.length)
    : 0;

  return (
    <div>
      <h2 className="admin-section-title">Traces</h2>

      {/* Stat cards */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="stat-card">
          <div className="stat-label">Traces</div>
          <div className="stat-value">{total.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Success rate</div>
          <div className="stat-value">
            {items.length ? `${((successCount / items.length) * 100).toFixed(1)}%` : '—'}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg duration</div>
          <div className="stat-value">{items.length ? fmtMs(avgDur) : '—'}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg tokens</div>
          <div className="stat-value">{items.length ? avgTokens.toLocaleString() : '—'}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar">
        <div className="admin-toolbar-controls">
          <div className="admin-period-btns">
            {PERIODS.map(({ label, value }) => (
              <button
                key={value}
                className={`admin-period-btn${period === value ? ' active' : ''}`}
                onClick={() => handlePeriod(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Filter by company ID…"
            aria-label="Filter by company ID"
            value={company}
            onChange={handleCompany}
            className="admin-company-input"
          />
          <select
            className="admin-source-select"
            aria-label="Filter by source"
            value={source}
            onChange={handleSource}
          >
            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button className="admin-export-btn" onClick={() => load()}>↻ Refresh</button>
        </div>
      </div>

      {error && (
        <div className="admin-error-state">
          <p>{error}</p>
          <button onClick={load}>Retry</button>
        </div>
      )}

      {/* Table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Source</th>
              <th>Question</th>
              <th>Duration</th>
              <th>Tokens</th>
              <th>Nodes</th>
              <th>Status</th>
              <th>Company</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: 'var(--admin-text-muted)' }}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '32px', color: 'var(--admin-text-muted)' }}>
                  No traces yet — send a query to generate the first trace.
                </td>
              </tr>
            )}
            {!loading && items.map(trace => (
              <React.Fragment key={trace.trace_id}>
                <tr
                  className={`admin-table-row clickable${selectedTrace?.trace_id === trace.trace_id ? ' selected' : ''}`}
                  onClick={() => setSelectedTrace(
                    selectedTrace?.trace_id === trace.trace_id ? null : trace
                  )}
                >
                  <td className="admin-td-muted">{fmtDate(trace.started_at)}</td>
                  <td><SourceBadge source={traceSource(trace)} /></td>
                  <td>{truncate(trace.question, 70)}</td>
                  <td>{fmtMs(trace.duration_ms)}</td>
                  <td>{(trace.total_tokens || 0).toLocaleString()}</td>
                  <td>
                    <span className="trace-node-path inline">
                      {(trace.node_path || []).map((n, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="trace-node-arrow">→</span>}
                          <span className="trace-node-pill">{n}</span>
                        </React.Fragment>
                      ))}
                    </span>
                  </td>
                  <td>
                    <span className={`trace-status-badge ${trace.success ? 'ok' : 'fail'}`}>
                      {trace.success ? 'ok' : 'error'}
                    </span>
                  </td>
                  <td className="admin-td-muted" title={trace.company_id}>
                    {resolveCompanyName(companyNames, trace.company_id)}
                  </td>
                </tr>
                {selectedTrace?.trace_id === trace.trace_id && (
                  <tr className="trace-detail-row">
                    <td colSpan={8}>
                      <TraceDetail trace={trace} onClose={() => setSelectedTrace(null)} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="admin-pagination">
          <button onClick={() => setPage(p => p - 1)} disabled={page === 0}>← Prev</button>
          <span>Page {page + 1} of {pageCount}</span>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= pageCount - 1}>Next →</button>
        </div>
      )}
    </div>
  );
}
